import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Switch, Table, Tag, TextField, Tooltip, Typography } from '@scale/scaleui';
import { ITeamLeadPodTypeJson } from '@scale/shared/types/expertManagement';
import { ITagJson } from '@scale/shared/types/tag';
import React, { useCallback, useState } from 'react';
import { LoadingTable } from 'react-scale/components/ExpertManagement/ExpertManagementPage/LoadingTable';
import { ToolsLink } from 'react-scale/components/ExpertManagement/ExpertManagementPage/ToolsLink';
import { useCreatePodType } from 'react-scale/components/ExpertManagement/hooks/mutations/useCreatePodType';
import { useUpdatePodType } from 'react-scale/components/ExpertManagement/hooks/mutations/useUpdatePodType';
import { useFetchPodTypes } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchPodTypes';
import TagSelector from 'react-scale/corp/components/UI/TagSelector';
import { twMerge } from 'tailwind-merge';

const DEFAULT_POD_TYPE: Partial<ITeamLeadPodTypeJson> = {
  name: '',
  tagPrefix: '',
  associatedTags: [],
  isPlatinum: false,
};

export const PodTypeManagement: React.FC = () => {
  const { data, isLoading, refetch } = useFetchPodTypes();
  const { mutateAsync: updatePodType, isLoading: updateLoading } = useUpdatePodType();
  const { mutateAsync: createPodType, isLoading: createLoading } = useCreatePodType();
  const [selectedPodType, setSelectedPodType] = useState<Partial<ITeamLeadPodTypeJson> | undefined>(
    undefined,
  );
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [editMode, setEditMode] = useState<'edit' | 'create'>('edit');
  const podTypeColumns = [
    {
      id: 'name',
      header: 'Name',
    },
    {
      id: 'tagPrefix',
      header: 'Tag Prefix',
    },
    {
      id: 'associatedTags',
      header: 'Associated Tags',
      cell: function NameCell(current: any) {
        const { associatedTags } = current.row.original;
        return (
          <div className="flex flex-col gap-2">
            {associatedTags.map((tag: ITagJson) => (
              <div key={tag._id}>
                <Tag variant="info">
                  <ToolsLink to={`/lookup/${tag._id}`}>{tag.name}</ToolsLink>
                </Tag>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      id: 'isPlatinum',
      header: 'Is Platinum',
      cell: function NameCell(current: any) {
        return <div>{current.row.original.isPlatinum ? 'Yes' : 'No'}</div>;
      },
    },
    {
      id: 'editButton',
      header: '',
      enableSorting: false,
      cell: function EditCell(current: any) {
        return (
          <Button
            onClick={() => {
              setEditMode('edit');
              setSelectedPodType(current.row.original);
              setDetailPanelOpen(true);
            }}
          >
            Edit
          </Button>
        );
      },
    },
  ];

  const closeModal = useCallback(() => {
    setDetailPanelOpen(false);
    setSelectedPodType(undefined);
  }, [setDetailPanelOpen, setSelectedPodType]);

  const editSelectedPodType = (fieldName: string, value: any) => {
    if (selectedPodType) {
      setSelectedPodType({ ...selectedPodType, [fieldName]: value });
    }
  };

  const onSave = useCallback(async () => {
    if (!selectedPodType) return;
    if (editMode === 'edit') {
      await updatePodType(selectedPodType as ITeamLeadPodTypeJson);
    } else {
      await createPodType(selectedPodType);
    }
    await refetch();
    closeModal();
  }, [selectedPodType, editMode, refetch, closeModal, updatePodType, createPodType]);

  const saving = updateLoading || createLoading;

  const renderInputField = (
    label: string | JSX.Element,
    component: JSX.Element,
    fullWidth: boolean = false,
  ) => {
    return !fullWidth ? (
      <div className="grid-cols-3 grid">
        <div className="flex my-auto">
          <Typography.Subtitle3 className="col-span-1">{label}</Typography.Subtitle3>
        </div>
        <div className="col-span-2">{component}</div>
      </div>
    ) : (
      <div className="flex flex-col gap-2">
        <Typography.Subtitle3>{label}</Typography.Subtitle3>
        <div>{component}</div>
      </div>
    );
  };

  return (
    <>
      <div style={{ minWidth: '70%' }}>
        <div className="flex justify-between my-2">
          <Typography.H5>Pod Types</Typography.H5>
          <Button
            onClick={() => {
              setEditMode('create');
              setSelectedPodType(DEFAULT_POD_TYPE);
              setDetailPanelOpen(true);
            }}
          >
            + Create
          </Button>
        </div>
        {isLoading || !data ? (
          <LoadingTable columns={podTypeColumns} numRows={3} rowHeight={2} />
        ) : (
          <Table columns={podTypeColumns} data={data.podTypes || []} />
        )}
      </div>
      <div
        className={twMerge(
          'scaleui fixed h-full top-0 right-0',
          detailPanelOpen ? 'w-[100%]' : 'w-0',
        )}
        style={{ backgroundColor: 'black', opacity: 0.2 }}
      />
      <div
        className={twMerge(
          'scaleui fixed h-full top-0 right-0 flex-col flex-grow overflow-y-hidden border-l bg-neutral-0 border-l-neutral-200 transition-all',
          detailPanelOpen ? 'w-[32%]' : 'w-0',
        )}
      >
        <div className="px-5 py-10 flex flex-col h-full justify-between">
          <div className="flex flex-col gap-4">
            {renderInputField(
              'Name',
              <TextField
                id="podName"
                value={selectedPodType?.name}
                placeholder="Tag name"
                onChange={e => editSelectedPodType('name', e.target.value)}
              />,
            )}
            {renderInputField(
              <Tooltip
                target={
                  <div className="flex gap-2">
                    Tag Prefix
                    <FontAwesomeIcon className="my-auto" icon="info-circle" />
                  </div>
                }
                text={
                  <div>
                    Prefix added to all pod leader tags (e.g. ptl_generalist_abcd_pod).
                    <br />
                    Changing this does NOT backfill any existing tags/pod leads.
                  </div>
                }
              />,
              <TextField
                id="tagPrefix"
                placeholder="Tag prefix"
                value={selectedPodType?.tagPrefix}
                onChange={e => editSelectedPodType('tagPrefix', e.target.value)}
              />,
            )}
            {selectedPodType &&
              renderInputField(
                <Tooltip
                  target={
                    <div className="flex gap-2">
                      Associated Tags
                      <FontAwesomeIcon className="my-auto" icon="info-circle" />
                    </div>
                  }
                  text={
                    <div>
                      Tags that will be automatically applied to any contributers being assigned to
                      leaders with this pod type.
                      <br />
                      Any changes performed here WILL be automatically applied to all current
                      contributors in the pod.
                    </div>
                  }
                />,
                <TagSelector
                  key="excluded-tags"
                  tags={(selectedPodType.associatedTags ?? []) as ITagJson[]}
                  onTagsChanged={e => editSelectedPodType('associatedTags', e)}
                />,
                true,
              )}
            {renderInputField(
              'Platinum',
              <Switch
                label="Is Platinum"
                checked={selectedPodType?.isPlatinum}
                onCheckedChange={e => editSelectedPodType('isPlatinum', e)}
              />,
            )}
          </div>
          <div className="flex flex-row gap-2 justify-end">
            <Button size="lg" disabled={saving} onClick={onSave}>
              {editMode === 'edit' ? 'Save Changes' : 'Create'}
            </Button>
            <Button size="lg" variant="secondary" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
